import {
  ORG_PAYMENT_TIER_DISPLAY_NAMES,
  OrgPaymentTier
} from '@kindo/universal';
import { signOut } from 'next-auth/react';

import {
  Button,
  ButtonType,
  Dropdown,
  Icon,
  Icons,
  Size,
  TextIconColor,
  Typography,
  TypographySize,
  TypographyWeight
} from '~/components/core';
import { SettingsTab } from '~/components/SettingsModal/SettingsModal.types';
import { LOGIN_ROUTE } from '~/constants';
import { useSettingsModal } from '~/contexts/SettingsModalContext';
import { useUserInfo } from '~/hooks';
import { useAppSelector } from '~/hooks';
import useGetCredits from '~/hooks/useGetOrgCredits';

const ProfileButton: React.FC = () => {
  const { user } = useUserInfo();
  const userName = user?.name;
  const userEmail = user?.email;

  const { orgCredits } = useGetCredits();

  // Redux
  const { org } = useAppSelector((state) => state.user);
  const activeTier = org?.paymentTier || OrgPaymentTier.FREE;
  const isEnterprise = activeTier === OrgPaymentTier.ENTERPRISE;

  const { openSettingsModal } = useSettingsModal();

  return (
    <Dropdown
      trigger={
        <div
          className="[&:hover_svg]:text-highlight flex h-fit w-fit shrink-0 items-center justify-center rounded-full p-[9px]"
          data-testid="profile-icon-container"
        >
          <Icons
            color={TextIconColor.PRIMARY}
            icon={Icon.PROFILE}
            size={Size.LARGE}
          />
        </div>
      }
    >
      <div
        className="flex w-max flex-col gap-1"
        data-testid="profile-dropdown-container"
      >
        {/* User Name + Email */}
        <div className="px-3" data-testid="user-info-wrapper">
          <div
            className="border-b-1 flex flex-col py-1"
            data-testid="user-info-container"
          >
            <Typography
              color={TextIconColor.PRIMARY}
              size={TypographySize.MEDIUM}
              weight={TypographyWeight.MEDIUM}
            >
              {userName || userEmail}
            </Typography>
            {userName && (
              <Typography
                color={TextIconColor.SECONDARY}
                size={TypographySize.SMALL}
                weight={TypographyWeight.LIGHT}
              >
                {userEmail}
              </Typography>
            )}
          </div>
          {/* Payment Tier + Credits Remaining */}
          <div
            className="border-b-1 flex flex-col gap-1 py-1"
            data-testid="account-info-container"
          >
            <div className="flex gap-2" data-testid="tier-upgrade-container">
              <div className="flex gap-1" data-testid="tier-container">
                <Typography
                  color={TextIconColor.PRIMARY}
                  size={TypographySize.SMALL}
                  weight={TypographyWeight.MEDIUM}
                >
                  Current plan:
                </Typography>
                <Typography
                  color={TextIconColor.PRIMARY}
                  size={TypographySize.SMALL}
                  weight={TypographyWeight.BOLD}
                >
                  {ORG_PAYMENT_TIER_DISPLAY_NAMES[activeTier]}
                </Typography>
              </div>
              {!isEnterprise && (
                <Button
                  label="Upgrade"
                  onClick={() => {
                    openSettingsModal(SettingsTab.MANAGE_SUBSCRIPTION);
                  }}
                  size={Size.SMALL}
                  type={ButtonType.OUTLINED}
                />
              )}
            </div>
            {orgCredits !== undefined && (
              <div className="flex gap-1" data-testid="account-info-details">
                <Typography
                  color={TextIconColor.PRIMARY}
                  size={TypographySize.SMALL}
                  weight={TypographyWeight.MEDIUM}
                >
                  Credits Remaining:
                </Typography>
                <Typography
                  color={TextIconColor.PRIMARY}
                  size={TypographySize.SMALL}
                  weight={TypographyWeight.BOLD}
                >
                  {/* Show commas */}
                  {orgCredits.toLocaleString()}
                </Typography>
              </div>
            )}
          </div>
        </div>
        {/* Sign Out */}
        <Button
          label="Sign Out"
          onClick={() => {
            void signOut({ callbackUrl: LOGIN_ROUTE });
          }}
          startIcon={Icon.LOGOUT}
          type={ButtonType.TEXT}
        />
      </div>
    </Dropdown>
  );
};

export default ProfileButton;
